<script lang="ts">
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import { CardGrid, Container, Modal, SelectCard, Text } from '@Shelly/shelly-design-system/index';
	import { profiles } from 'src/stores/features/profiles';
	import { format } from 'src/services/i18n';
	import { capFirstChar } from 'src/helpers/utilities';
	import { getProfileDisplayName } from 'src/helpers/houses/homes_helpers';
	import { createEventDispatcher, onMount } from 'svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { MenuListDataType } from './DropdownItem.types';

	export let showModal;
	const dispatch = createEventDispatcher<{
		add_another_profile: void;
		selectProfile: MenuListDataType;
	}>();

	let windowClientWidth: number;
	$: activeProfile = $profiles.find((item) => item.isSelected);
	$: profilesList = $profiles.filter((item) => item !== activeProfile);

	/**
	 * The confirm-logout click that opens this modal can land on the first
	 * account card in the same gesture. Only honor a click that also started
	 * on that card after the opening pointer event has finished.
	 */
	let acceptSelection = false;
	let pointerDownTarget: string | null = null;

	onMount(() => {
		const timeout = window.setTimeout(() => {
			acceptSelection = true;
		}, 350);
		return () => window.clearTimeout(timeout);
	});

	const onCloseModal = () => {
		showModal = false;
	};

	const onSelectCardClick = (profileEmail: string) => {
		if (!acceptSelection || pointerDownTarget !== profileEmail) {
			pointerDownTarget = null;
			return;
		}
		pointerDownTarget = null;

		const newProfileToLogin = $profiles.find(
			(profile) => profile.email === profileEmail && !profile.isSelected,
		);
		if (!newProfileToLogin) return;

		onCloseModal();
		dispatch('selectProfile', newProfileToLogin);
	};

	const onAddProfileClick = () => {
		if (!acceptSelection || pointerDownTarget !== 'add') {
			pointerDownTarget = null;
			return;
		}
		pointerDownTarget = null;
		dispatch('add_another_profile');
	};

	$: isMobile = windowClientWidth < SystemScreenBreakpoints.MOBILE_MAX;
</script>

<svelte:window bind:innerWidth={windowClientWidth} />

<Modal
	{showModal}
	title={$format('log_in_another_acc_title', {
		default: 'Log In Another Account',
	})}
	on:close={onCloseModal}
	removeScrollPadding
>
	<Container slot="content" padding="scrollPaddings">
		<Text class="mb-m" styleType="h2">
			{$format('choose_another_acc_login_prompt', {
				default: 'Choose another account to log in to',
			})}
		</Text>
		<CardGrid gridGap="s" sm={200} md={200} xl={200} noPaddingBottom>
			{#each profilesList as profile}
				{@const profileTitle = getProfileDisplayName(profile.title, profile.email ?? '')}
				<div
					role="presentation"
					on:pointerdown={() => {
						pointerDownTarget = profile.email ?? '';
					}}
				>
					<SelectCard
						cardBgColor={$showNewLayout ? 'color-1' : undefined}
						hideAction
						type={isMobile ? 'horizontal' : 'vertical'}
						class="width-full"
						image={profile?.acc_avatar?.image}
						title={profileTitle}
						backgroundColor={profile?.acc_avatar?.backgroundColor}
						circleAvatarText={profile?.acc_avatar?.text}
						on:click={() => onSelectCardClick(profile.email)}
						id={`login-another-profile-card-${profile.id}`}
						aria-label={$format('label_select_profile', {
							default: `Select profile ${profileTitle}`,
						})}
					/>
				</div>
			{/each}
			<div
				role="presentation"
				on:pointerdown={() => {
					pointerDownTarget = 'add';
				}}
			>
				<SelectCard
					cardBgColor={$showNewLayout ? 'color-1' : undefined}
					hideAction
					type={isMobile ? 'horizontal' : 'vertical'}
					class="width-full"
					icon="fat fa-plus"
					id="login-another-profile-add-card"
					aria-label={$format('button_profile_add', {
						default: 'Add another profile',
					})}
					title={capFirstChar(
						$format('button_profile_add', {
							default: 'Add another profile',
						}),
					)}
					on:click={onAddProfileClick}
				/>
			</div>
		</CardGrid>
	</Container>
</Modal>
